const SCENARIO_ORDER = ["Sin reforma", "Base reformas", "Custom reformas"];

const getScenarioCategory = (name) => {
    if (name.toLowerCase().includes('sin reforma')) return "Sin reforma";
    if (name.toLowerCase().includes('base reforma')) return "Base reformas";
    return "Custom reformas"; // All other reformas are custom
};

const getScenarioSubCategory = (name) => {
    if (name.toLowerCase().includes('sin reforma')) return 0;
    if (name.toLowerCase().includes('base reforma estándar')) return 1;
    if (name.toLowerCase().includes('base reforma premium')) return 2;
    // For custom reformas, sort by cost (will be handled in the main sorting)
    return 3;
};

const getConstructorOrder = (constructor) => {
    if (constructor === 'Sin constructor') return 0;
    if (constructor === 'Genérico calidad estándar') return 1;
    return 2; // Custom constructors
};

export const normalizeScenarioName = (name) => {
    return name.replace(" Base", "").replace(" IA", "");
};

export const getScenarioIcon = (name) => {
    if (name.toLowerCase().includes('vacacional')) return '🌴';
    if (name.toLowerCase().includes('premium')) return '💎';
    if (name.toLowerCase().includes('estándar')) return '🛠️';
    if (name.toLowerCase().includes('mínima')) return '🔧';
    if (name.toLowerCase().includes('sin reforma')) return '🏠';
    return '🏗️';
};

export const findBestOverall = (normalizedItems, isVenta = true) => {
    return normalizedItems.reduce((best, item) => {
        const val = parseFloat(isVenta ? item.venta?.rentabilidadAnual : item.alquiler?.rentabilidadAnual);
        if (isNaN(val)) return best;
        if (!best) return item;
        const bestVal = parseFloat(isVenta ? best.venta?.rentabilidadAnual : best.alquiler?.rentabilidadAnual);
        return val > bestVal ? item : best;
    }, null);
};

export const processScenariosData = (allItems) => {
    const originalScenarios = allItems.filter(item => !item.tipoReforma.nombre.includes("IA"));
    const iaScenarios = allItems.filter(item => item.tipoReforma.nombre.includes("IA"));

    const normalizedItems = originalScenarios.map(original => {
        const normalizedName = normalizeScenarioName(original.tipoReforma.nombre);
        
        // Check if this is a private/custom reform template (not a standard template)
        const isPrivateTemplate = original.coleccion_nombre !== "Base Reforma Estándar" && 
                                 original.coleccion_nombre !== "Base Reforma Premium";
        
        // Only match with IA if it's not a private template
        const matchingIA = !isPrivateTemplate ? 
            iaScenarios.find(ia => normalizeScenarioName(ia.tipoReforma.nombre) === normalizedName && ia.constructor === original.constructor) : 
            null;
        
        return {
            ...original,
            normalizedName,
            ia: matchingIA,
            isPrivateTemplate
        };
    });

    // Add IA scenarios that didn't have a matching base scenario
    iaScenarios.forEach(ia => {
        const normalizedName = normalizeScenarioName(ia.tipoReforma.nombre);
        if (!normalizedItems.some(item => item.normalizedName === normalizedName && item.constructor === ia.constructor)) {
            normalizedItems.push({
                ...ia,
                normalizedName,
                ia: null, // No matching base, but might have original cost data
            });
        }
    });

    return normalizedItems;
};

export const sortScenariosAndConstructors = (normalizedItems) => {
    // Group by scenario name
    const scenariosByName = {};
    normalizedItems.forEach(item => {
        if (!scenariosByName[item.normalizedName]) {
            scenariosByName[item.normalizedName] = [];
        }
        scenariosByName[item.normalizedName].push(item);
    });

    // Get all unique constructors and sort them
    const allConstructors = [...new Set(normalizedItems.map(item => item.constructor || 'Sin constructor'))]
        .sort((a, b) => {
            const orderA = getConstructorOrder(a);
            const orderB = getConstructorOrder(b);
            if (orderA !== orderB) return orderA - orderB;
            // If both are custom constructors, sort alphabetically
            if (orderA === 2 && orderB === 2) return a.localeCompare(b);
            return 0;
        });

    // Sort scenarios by category and cost
    const sortedScenarioNames = Object.keys(scenariosByName).sort((a, b) => {
        const categoryA = getScenarioCategory(a);
        const categoryB = getScenarioCategory(b);
        
        // First sort by category
        const orderA = SCENARIO_ORDER.indexOf(categoryA);
        const orderB = SCENARIO_ORDER.indexOf(categoryB);
        if (orderA !== orderB) return orderA - orderB;
        
        // Within same category, sort by sub-category for base reformas
        if (categoryA === "Base reformas" && categoryB === "Base reformas") {
            const subOrderA = getScenarioSubCategory(a);
            const subOrderB = getScenarioSubCategory(b);
            if (subOrderA !== subOrderB) return subOrderA - subOrderB;
        }
        
        // Within same category/sub-category, sort by cost (lowest first)
        const itemsA = scenariosByName[a];
        const itemsB = scenariosByName[b];
        const minCostA = Math.min(...itemsA.map(item => parseFloat(item.costeReforma) || 0));
        const minCostB = Math.min(...itemsB.map(item => parseFloat(item.costeReforma) || 0));
        return minCostA - minCostB;
    });

    return {
        scenariosByName,
        allConstructors,
        sortedScenarioNames
    };
};

export const formatCurrency = value => {
    if (!value || isNaN(value)) return '0 €';
    return parseFloat(value).toLocaleString('es-ES', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 2 
    }) + ' €';
};

export const getScenarioData = (item, isVenta = true) => {
    const hasIA = !!item.ia;
    const costeActual = hasIA ? item.ia.costeReforma : item.costeReforma;
    const mainValue = isVenta ? item.venta?.rentabilidadAnual : item.alquiler?.rentabilidadAnual;
    const iaMainValue = isVenta ? item.ia?.venta?.rentabilidadAnual : item.ia?.alquiler?.rentabilidadAnual;
    const subValue = isVenta ? null : (item.alquiler?.tiempoRetornoMeses ? `${item.alquiler.tiempoRetornoMeses} meses` : null);
    const iaSubValue = isVenta ? null : (item.ia?.alquiler?.tiempoRetornoMeses ? `${item.ia.alquiler?.tiempoRetornoMeses} meses` : null);

    return {
        hasIA,
        costeActual,
        mainValue,
        iaMainValue,
        subValue,
        iaSubValue
    };
}; 